import React, { useRef, useState, useMemo, useEffect } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Page from "../components/Page";
import {
  IconCash,
  IconUser,
  IconCalendar,
  IconReceipt2,
  IconSearch,
  IconTrendingUp,
  IconCreditCard,
  IconX,
  IconUsers,
  IconFileSpreadsheet,
  IconFileDownload,
  IconPrinter,
  IconArrowLeft,
  IconCheck,
  IconPhone,
  IconMail,
  IconId,
  IconLayersSubtract,
  IconCoins,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconEye,
  IconTrash,
  IconPlus,
  IconFileText
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import {
  useInvoices,
  collectDue,
  useCustomerDues,
  collectCustomerDue,
  addManualCustomerDue,
  useCustomerStatement,
  getInvoiceOrders
} from "../controllers/invoices.controller";
import { useCustomers } from "../controllers/customers.controller";
import { usePaymentTypes, useStoreSettings, usePrintSettings } from "../controllers/settings.controller";
import { CURRENCIES } from "../config/currencies.config";
import { toast } from "react-hot-toast";
import {
  exportStatementToPDF,
  exportStatementToExcel,
  printStatementHTML
} from "../helpers/StatementExportHelper";
import { printReceipt, printPaymentVoucher } from "../helpers/PrinterHelper";
import { setDetailsForReceiptPrint } from "../helpers/ReceiptHelper";

export default function AccountsReceivablePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // View state: "customers" | "invoices" | "statement"
  const [activeTab, setActiveTab] = useState("customers");
  const [selectedCustomerForStatement, setSelectedCustomerForStatement] = useState(null);

  // Search & Filters
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState({
    from: "",
    to: "",
  });

  // Pagination states
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPageSize, setCustomerPageSize] = useState(12);

  const [invoicesPage, setInvoicesPage] = useState(1);
  const [invoicesPageSize, setInvoicesPageSize] = useState(20);

  const [statementPage, setStatementPage] = useState(1);
  const [statementPageSize, setStatementPageSize] = useState(25);

  // Modal states
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCustomerForBulkPay, setSelectedCustomerForBulkPay] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().substring(0, 10));
  const [payMethod, setPayMethod] = useState("Cash");
  const [payNotes, setPayNotes] = useState("");
  const [isSubmittingPay, setIsSubmittingPay] = useState(false);

  // Split payment state
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]);

  // Detail viewing & voucher states
  const [viewingInvoiceDetails, setViewingInvoiceDetails] = useState(null);
  const [clearedPaymentReceipt, setClearedPaymentReceipt] = useState(null);

  // Manual Due modal state
  const [manualDueForm, setManualDueForm] = useState({
    customer_id: "",
    amount: "",
    date: new Date().toISOString().substring(0, 10),
    notes: "Manual Due Entry"
  });

  // Store settings & Currency
  const { data: storeSettings } = useStoreSettings();
  const { data: printSettings } = usePrintSettings();
  const currencySymbol = useMemo(() => {
    const cur = CURRENCIES.find((c) => c.cc === storeSettings?.currency);
    return cur?.symbol || storeSettings?.currency || "Rs.";
  }, [storeSettings]);

  // Data queries
  const invoiceType = (dateFilter.from && dateFilter.to) ? "custom" : "all";
  const { data: invoicesData, isLoading: isLoadingInvoices, mutate: mutateInvoices } = useInvoices({
    type: invoiceType,
    from: dateFilter.from || null,
    to: dateFilter.to || null,
  });

  const {
    data: customerDues,
    isLoading: isLoadingCustomerDues,
    mutate: mutateCustomerDues
  } = useCustomerDues(search);

  const { data: paymentTypes } = usePaymentTypes();
  const { data: customersResponse } = useCustomers({ perPage: 1000 });

  const customerList = useMemo(() => {
    if (!customersResponse) return [];
    if (Array.isArray(customersResponse)) return customersResponse;
    if (Array.isArray(customersResponse.customers)) return customersResponse.customers;
    return [];
  }, [customersResponse]);

  // Statement query
  const {
    data: statementData,
    isLoading: isLoadingStatement,
    mutate: mutateStatement
  } = useCustomerStatement(
    selectedCustomerForStatement?.customer_id || null,
    dateFilter.from,
    dateFilter.to
  );

  // Read URL query parameter for direct customer ledger link
  useEffect(() => {
    const cId = searchParams.get("customerId");
    if (cId && customerList && customerList.length > 0) {
      const match = customerList.find((c) => String(c.id || c.phone) === String(cId));
      if (match) {
        setSelectedCustomerForStatement({
          customer_id: match.phone || match.id,
          name: match.name,
          phone: match.phone,
          email: match.email,
          address: match.address
        });
        setActiveTab("statement");
      }
    }
  }, [searchParams, customerList]);

  // Filtered due invoices
  const dueInvoices = useMemo(() => {
    if (!invoicesData || !Array.isArray(invoicesData)) return [];
    return invoicesData.filter((inv) => {
      const due = Number(String(inv.due_amount).replace(/,/g, "")) || 0;
      if (due <= 0) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        inv.name?.toLowerCase().includes(q) ||
        inv.customer_id?.toLowerCase().includes(q) ||
        String(inv.invoice_id).includes(q)
      );
    });
  }, [invoicesData, search]);

  // Pagination computations
  const totalCustomerPages = Math.max(1, Math.ceil((customerDues?.length || 0) / customerPageSize));
  const safeCustomerPage = Math.min(customerPage, totalCustomerPages);
  const paginatedCustomerDues = useMemo(() => {
    return (customerDues || []).slice((safeCustomerPage - 1) * customerPageSize, safeCustomerPage * customerPageSize);
  }, [customerDues, safeCustomerPage, customerPageSize]);

  const totalInvoicesPages = Math.max(1, Math.ceil((dueInvoices?.length || 0) / invoicesPageSize));
  const safeInvoicesPage = Math.min(invoicesPage, totalInvoicesPages);
  const paginatedDueInvoices = useMemo(() => {
    return (dueInvoices || []).slice((safeInvoicesPage - 1) * invoicesPageSize, safeInvoicesPage * invoicesPageSize);
  }, [dueInvoices, safeInvoicesPage, invoicesPageSize]);

  const statementTransactions = useMemo(() => {
    const list = statementData?.transactions || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((t) => {
      return (
        t.description?.toLowerCase().includes(q) ||
        t.notes?.toLowerCase().includes(q) ||
        String(t.invoice_id || "").toLowerCase().includes(q) ||
        String(t.invoice_no || "").toLowerCase().includes(q) ||
        String(t.id || "").toLowerCase().includes(q) ||
        String(t.payment_method || "").toLowerCase().includes(q)
      );
    });
  }, [statementData, search]);

  const totalStatementPages = Math.max(1, Math.ceil(statementTransactions.length / statementPageSize));
  const safeStatementPage = Math.min(statementPage, totalStatementPages);
  const paginatedStatementTransactions = useMemo(() => {
    return statementTransactions.slice((safeStatementPage - 1) * statementPageSize, safeStatementPage * statementPageSize);
  }, [statementTransactions, safeStatementPage, statementPageSize]);

  // Overall Financial Metrics
  const metrics = useMemo(() => {
    let totalReceivables = 0;
    let totalInvoiced = 0;
    let totalCollected = 0;

    if (invoicesData && Array.isArray(invoicesData)) {
      invoicesData.forEach((inv) => {
        const total = Number(String(inv.total).replace(/,/g, "")) || 0;
        const paid = Number(String(inv.paid_amount).replace(/,/g, "")) || 0;
        const due = Number(String(inv.due_amount).replace(/,/g, "")) || 0;
        totalInvoiced += total;
        totalCollected += paid;
        totalReceivables += due;
      });
    }

    const customersWithDue = customerDues ? customerDues.length : 0;
    return { totalReceivables, totalInvoiced, totalCollected, customersWithDue };
  }, [invoicesData, customerDues]);

  // FIFO Allocation calculation in real-time for customer bulk settlement
  const fifoAllocations = useMemo(() => {
    if (!selectedCustomerForBulkPay) return [];
    const amountToApply = parseFloat(String(payAmount).replace(/,/g, "")) || 0;
    if (amountToApply <= 0) return [];

    // Filter unpaid invoices for this customer sorted oldest date first
    const custInvoices = (invoicesData || [])
      .filter((inv) => String(inv.customer_id) === String(selectedCustomerForBulkPay.customer_id) && (Number(String(inv.due_amount).replace(/,/g, "")) || 0) > 0)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    let remaining = amountToApply;
    const allocations = [];

    for (const inv of custInvoices) {
      if (remaining <= 0) break;
      const due = Number(String(inv.due_amount).replace(/,/g, "")) || 0;
      const allocated = Math.min(remaining, due);
      const remainingDue = Math.max(0, due - allocated);
      const isCleared = remainingDue <= 0;

      allocations.push({
        invoice_id: inv.invoice_id,
        date: inv.created_at,
        original_due: due,
        allocated,
        remaining_due: remainingDue,
        is_cleared: isCleared
      });

      remaining -= allocated;
    }

    return allocations;
  }, [selectedCustomerForBulkPay, payAmount, invoicesData]);

  // Split payment row handlers
  const handleAddSplitRow = () => {
    const activePts = paymentTypes?.filter(pt => pt.is_active) || paymentTypes || [];
    const firstPt = activePts[0];
    setSplitPayments(prev => [
      ...prev,
      { method: firstPt?.title || "Cash", amount: 0, payment_type_id: firstPt?.id }
    ]);
  };

  const handleUpdateSplitRow = (index, field, value) => {
    setSplitPayments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === "amount") {
        const sum = updated.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
        setPayAmount(sum > 0 ? Number(sum.toFixed(2)) : "");
      }
      return updated;
    });
  };

  const handleRemoveSplitRow = (index) => {
    setSplitPayments(prev => {
      const updated = prev.filter((_, i) => i !== index);
      const sum = updated.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
      setPayAmount(sum > 0 ? Number(sum.toFixed(2)) : "");
      return updated;
    });
  };

  // Open single invoice collect modal
  const btnShowSingleCollectModal = (inv) => {
    setSelectedInvoice(inv);
    setSelectedCustomerForBulkPay(null);
    const amt = Number(String(inv.due_amount).replace(/,/g, "")) || 0;
    setPayAmount(amt);
    setPayNotes("");
    setIsSplitPayment(false);
    setSplitPayments([
      { method: payMethod || "Cash", amount: amt, payment_type_id: paymentTypes?.[0]?.id }
    ]);
    document.getElementById("modal-collect-due").showModal();
  };

  // Open bulk FIFO modal
  const btnShowBulkCollectModal = (cust) => {
    setSelectedCustomerForBulkPay(cust);
    setSelectedInvoice(null);
    const amt = Number(String(cust.total_due || 0).replace(/,/g, "")) || 0;
    setPayAmount(amt);
    setPayNotes("");
    setIsSplitPayment(false);
    setSplitPayments([
      { method: payMethod || "Cash", amount: amt, payment_type_id: paymentTypes?.[0]?.id }
    ]);
    document.getElementById("modal-bulk-collect-fifo").showModal();
  };

  // Open statement view
  const btnOpenStatement = (cust) => {
    setSelectedCustomerForStatement(cust);
    setActiveTab("statement");
  };

  // Process single invoice collection
  const btnProcessSingleCollection = async () => {
    const totalToPay = parseFloat(String(payAmount).replace(/,/g, "")) || 0;
    if (!selectedInvoice || totalToPay <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (isSplitPayment) {
      const splitSum = splitPayments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
      if (Math.abs(splitSum - totalToPay) > 0.01) {
        toast.error(`Split payments total (${currencySymbol}${splitSum.toFixed(2)}) must match collection amount (${currencySymbol}${totalToPay.toFixed(2)})`);
        return;
      }
    }

    try {
      setIsSubmittingPay(true);
      toast.loading("Collecting invoice payment...");
      const res = await collectDue({
        invoice_id: selectedInvoice.invoice_id,
        amount: totalToPay,
        payment_date: payDate,
        payment_method: payMethod,
        notes: payNotes || `Payment for Invoice #${selectedInvoice.invoice_id}`,
        multi_payments: isSplitPayment ? splitPayments : null
      });

      toast.dismiss();
      toast.success(res?.data?.message || "Payment recorded successfully!");
      mutateInvoices();
      mutateCustomerDues();
      if (selectedCustomerForStatement) mutateStatement();
      document.getElementById("modal-collect-due").close();

      const remainingDue = Math.max(0, (Number(String(selectedInvoice.due_amount).replace(/,/g, "")) || 0) - totalToPay);
      setClearedPaymentReceipt({
        voucherNo: 'RCP-' + Date.now().toString().slice(-6),
        title: 'INVOICE PAYMENT RECEIPT',
        date: payDate,
        partyName: selectedInvoice.name || 'Walk-in',
        partyPhone: selectedInvoice.customer_id,
        partyType: 'Customer',
        invoicesSettled: [{
          invoice_id: selectedInvoice.invoice_id,
          original_due: Number(String(selectedInvoice.due_amount).replace(/,/g, "")) || 0,
          allocated: totalToPay,
          remaining_due: remainingDue
        }],
        paymentMethods: isSplitPayment ? splitPayments.map(p => ({ method: p.method, amount: parseFloat(p.amount) || 0 })) : [{ method: payMethod, amount: totalToPay }],
        totalAmount: totalToPay,
        remainingDue: remainingDue,
        notes: payNotes,
        cashier: ''
      });
      document.getElementById("modal-payment-clearance-voucher").showModal();
      setSelectedInvoice(null);
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error recording payment");
    } finally {
      setIsSubmittingPay(false);
    }
  };

  // Process Bulk FIFO Settlement
  const btnProcessBulkFIFOSettlement = async () => {
    const totalToPay = parseFloat(String(payAmount).replace(/,/g, "")) || 0;
    if (!selectedCustomerForBulkPay || totalToPay <= 0) {
      toast.error("Please enter a valid collection amount");
      return;
    }

    if (isSplitPayment) {
      const splitSum = splitPayments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
      if (Math.abs(splitSum - totalToPay) > 0.01) {
        toast.error(`Split payments total (${currencySymbol}${splitSum.toFixed(2)}) must match collection amount (${currencySymbol}${totalToPay.toFixed(2)})`);
        return;
      }
    }

    try {
      setIsSubmittingPay(true);
      toast.loading("Settling customer invoices via FIFO...");
      const res = await collectCustomerDue({
        customer_id: selectedCustomerForBulkPay.customer_id,
        amount: totalToPay,
        payment_method: payMethod,
        notes: payNotes || "FIFO Bulk Due Collection",
        multi_payments: isSplitPayment ? splitPayments : null,
        payment_date: payDate
      });

      toast.dismiss();
      toast.success(res?.data?.message || "Customer dues settled with FIFO successfully!");
      mutateInvoices();
      mutateCustomerDues();
      if (selectedCustomerForStatement) mutateStatement();
      document.getElementById("modal-bulk-collect-fifo").close();

      const settledInvoices = res.data?.allocations?.map(a => ({
        invoice_id: a.invoice_id,
        original_due: a.original_due,
        allocated: a.amount_applied,
        remaining_due: a.remaining_due
      })) || fifoAllocations;

      const remainingCustomerDue = res.data?.total_due !== undefined
        ? res.data.total_due
        : Math.max(0, (Number(String(selectedCustomerForBulkPay.total_due || 0).replace(/,/g, "")) || 0) - totalToPay);

      setClearedPaymentReceipt({
        voucherNo: 'RCP-' + Date.now().toString().slice(-6),
        title: 'CUSTOMER DUE CLEARANCE (FIFO)',
        date: payDate,
        partyName: res.data?.customer_name || selectedCustomerForBulkPay.customer_name || selectedCustomerForBulkPay.name,
        partyPhone: res.data?.customer_phone || selectedCustomerForBulkPay.customer_id || selectedCustomerForBulkPay.phone,
        partyType: 'Customer',
        invoicesSettled: settledInvoices,
        paymentMethods: isSplitPayment ? splitPayments.map(p => ({ method: p.method, amount: parseFloat(p.amount) || 0 })) : [{ method: payMethod, amount: totalToPay }],
        totalAmount: totalToPay,
        remainingDue: remainingCustomerDue,
        notes: payNotes,
        cashier: ''
      });
      document.getElementById("modal-payment-clearance-voucher").showModal();
      setSelectedCustomerForBulkPay(null);
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error recording bulk payment");
    } finally {
      setIsSubmittingPay(false);
    }
  };

  // View Invoice Details
  const btnViewInvoiceDetails = async (invoiceId) => {
    try {
      toast.loading("Loading invoice details...");
      const res = await getInvoiceOrders(invoiceId);
      toast.dismiss();
      if (res.status === 200) {
        setViewingInvoiceDetails({
          invoiceId,
          ...res.data
        });
        document.getElementById("modal-invoice-detail-viewer").showModal();
      }
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to load invoice details");
    }
  };

  // Thermal Print Invoice
  const btnPrintInvoiceThermal = async (invoiceId, tRecord) => {
    try {
      toast.loading("Preparing thermal invoice...");
      const res = await getInvoiceOrders(invoiceId);
      toast.dismiss();
      if (res.status === 200) {
        const {
          subtotal,
          taxTotal,
          serviceChargeTotal,
          total,
          discount,
          roundOff,
          paidAmount,
          dueAmount,
          orders: ordersArr = []
        } = res.data;

        const orders = [];
        const orderIds = (ordersArr || []).map(o => o.id).join(", ");
        for (const o of ordersArr) {
          (o.items || []).forEach(i => {
            orders.push({
              ...i,
              title: i.item_title,
              addons_ids: i.addons?.length > 0 ? i.addons.map(a => a.id) : [],
              variant: i.variant_id ? { id: i.variant_id, title: i.variant_title, price: i.variant_price } : null
            });
          });
        }

        const billFormat = localStorage.getItem("bill_format") || printSettings?.bill_format || "retail_tax_invoice";
        const pageFormat = printSettings?.page_format || localStorage.getItem("page_format") || "58";

        const receiptDetails = {
          cartItems: orders,
          customerType: "CUSTOMER",
          customer: {
            id: selectedCustomerForStatement?.customer_id || selectedCustomerForStatement?.phone,
            name: selectedCustomerForStatement?.customer_name || selectedCustomerForStatement?.name
          },
          tableId: null,
          currency: currencySymbol,
          storeSettings,
          printSettings,
          billFormat,
          pageFormat,
          itemsTotal: subtotal,
          taxTotal,
          serviceChargeTotal,
          payableTotal: total,
          storedDiscount: discount || 0,
          roundOff: roundOff || 0,
          paidAmount: paidAmount !== undefined ? paidAmount : total,
          dueAmount: dueAmount || 0,
          orderId: orderIds || String(invoiceId),
          created_at: tRecord?.date || new Date(),
          date: tRecord?.date || new Date(),
          invoiceId,
          invoice_id: invoiceId,
          billNo: invoiceId,
          invoiceNo: invoiceId,
          paymentMethod: (paidAmount > 0 && dueAmount <= 0) ? "Paid" : (paidAmount > 0 ? "Partial" : "Due")
        };

        setDetailsForReceiptPrint(receiptDetails);
        await printReceipt(receiptDetails);
        toast.success("Thermal invoice sent to printer!");
      }
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error("Failed to print thermal invoice");
    }
  };

  // Thermal Print Payment Voucher
  const btnPrintPaymentVoucherReceipt = async (voucher) => {
    try {
      toast.loading("Printing payment receipt...");
      await printPaymentVoucher({
        ...voucher,
        storeSettings,
        currency: currencySymbol,
        pageFormat: printSettings?.page_format || localStorage.getItem("page_format") || "58"
      });
      toast.dismiss();
      toast.success("Payment voucher sent to printer!");
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error("Failed to print voucher");
    }
  };

  // Process Manual Due Entry
  const btnProcessManualDue = async () => {
    if (!manualDueForm.customer_id || !manualDueForm.amount || parseFloat(manualDueForm.amount) <= 0) {
      toast.error("Please select a customer and valid amount");
      return;
    }
    try {
      toast.loading("Recording manual due...");
      const res = await addManualCustomerDue({
        customer_id: manualDueForm.customer_id,
        amount: parseFloat(manualDueForm.amount),
        date: manualDueForm.date,
        notes: manualDueForm.notes || "Manual Due Entry"
      });

      toast.dismiss();
      toast.success("Manual due added successfully!");
      mutateInvoices();
      mutateCustomerDues();
      if (selectedCustomerForStatement) mutateStatement();
      document.getElementById("modal-manual-due").close();
      setManualDueForm({
        customer_id: "",
        amount: "",
        date: new Date().toISOString().substring(0, 10),
        notes: "Manual Due Entry"
      });
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error adding manual due");
    }
  };

  // Export handlers
  const handleExportPDF = () => {
    if (!statementData || !statementData.customer) {
      toast.error("No statement data to export");
      return;
    }
    exportStatementToPDF({
      type: "Customer",
      party: statementData.customer,
      storeSettings,
      transactions: statementData.transactions || [],
      summary: statementData.summary || {},
      dateRange: dateFilter.from && dateFilter.to ? `${dateFilter.from} to ${dateFilter.to}` : "All Time",
      currency: currencySymbol
    });
  };

  const handleExportExcel = () => {
    if (!statementData || !statementData.customer) {
      toast.error("No statement data to export");
      return;
    }
    exportStatementToExcel({
      type: "Customer",
      party: statementData.customer,
      storeSettings,
      transactions: statementData.transactions || [],
      summary: statementData.summary || {},
      dateRange: dateFilter.from && dateFilter.to ? `${dateFilter.from} to ${dateFilter.to}` : "All Time",
      currency: currencySymbol
    });
  };

  const handlePrint = () => {
    if (!statementData || !statementData.customer) {
      toast.error("No statement data to print");
      return;
    }
    printStatementHTML({
      type: "Customer",
      party: statementData.customer,
      storeSettings,
      transactions: statementData.transactions || [],
      summary: statementData.summary || {},
      dateRange: dateFilter.from && dateFilter.to ? `${dateFilter.from} to ${dateFilter.to}` : "All Time",
      currency: currencySymbol
    });
  };

  return (
    <Page>
      {/* ──────────────── Top Navigation & Header ──────────────── */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8 gap-6">
        <div>
          <div className="flex items-center gap-3">
            {activeTab === "statement" && (
              <button
                onClick={() => setActiveTab("customers")}
                className="p-2.5 rounded-2xl bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition shadow-sm"
                title="Back to Customers"
              >
                <IconArrowLeft size={20} stroke={iconStroke} />
              </button>
            )}
            <div>
              <h2 className="text-3xl font-black tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
                Accounts Receivable <span className="text-blue-600 font-bold text-lg">(Customer Dues)</span>
              </h2>
              <p className="text-gray-500 text-sm mt-0.5">
                {activeTab === "statement"
                  ? `Detailed account ledger & statement for ${selectedCustomerForStatement?.customer_name || selectedCustomerForStatement?.name}`
                  : "Manage customer credit, collect dues with FIFO allocation, and generate debtor statements"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          <button
            onClick={() => document.getElementById("modal-manual-due").showModal()}
            className="px-5 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400 dark:hover:bg-blue-900/40 font-bold text-sm rounded-2xl border border-blue-200 dark:border-blue-900/50 transition flex items-center gap-2 shadow-sm"
          >
            + Add Manual Due
          </button>

          {activeTab !== "statement" && (
            <div className="join bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 p-1 rounded-2xl shadow-sm">
              <button
                onClick={() => setActiveTab("customers")}
                className={`join-item px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition ${
                  activeTab === "customers"
                    ? "bg-blue-600 text-white shadow"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <IconUsers size={16} /> By Customer ({customerDues?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab("invoices")}
                className={`join-item px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition ${
                  activeTab === "invoices"
                    ? "bg-blue-600 text-white shadow"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <IconReceipt2 size={16} /> All Invoices ({dueInvoices?.length || 0})
              </button>
            </div>
          )}

          {activeTab === "statement" && (
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-bold rounded-2xl transition flex items-center gap-1.5 shadow-sm"
              >
                <IconPrinter size={16} /> Print
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 text-xs font-bold rounded-2xl transition flex items-center gap-1.5 shadow-sm shadow-blue-200 dark:shadow-none"
              >
                <IconFileDownload size={16} /> PDF Statement
              </button>
              <button
                onClick={handleExportExcel}
                className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-bold rounded-2xl transition flex items-center gap-1.5 shadow-sm shadow-emerald-200 dark:shadow-none"
              >
                <IconFileSpreadsheet size={16} /> Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ──────────────── Key Metrics Summary Cards ──────────────── */}
      {activeTab !== "statement" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {/* Card 1: Total Receivables */}
          <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 p-6 rounded-[2rem] text-white shadow-xl shadow-blue-100 dark:shadow-none relative overflow-hidden">
            <div className="absolute right-4 top-4 text-white/10">
              <IconTrendingUp size={80} />
            </div>
            <div className="flex items-center gap-2 opacity-85 text-xs font-black uppercase tracking-widest mb-2">
              <IconTrendingUp size={18} /> Total Outstanding Receivables
            </div>
            <div className="text-3xl font-black tracking-tight">
              <span className="text-lg font-normal opacity-80 mr-1">{currencySymbol}</span>
              {metrics.totalReceivables.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] text-blue-100 font-medium mt-2">Uncollected credit balance from customers</p>
          </div>

          {/* Card 2: Total Invoiced Sales */}
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-black uppercase tracking-widest mb-2">
              <IconReceipt2 size={18} className="text-purple-500" /> Total Sales Invoiced
            </div>
            <div className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
              <span className="text-lg font-normal text-gray-400 mr-1">{currencySymbol}</span>
              {metrics.totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] text-gray-400 font-medium mt-2">Total invoiced sales volume</p>
          </div>

          {/* Card 3: Total Collected */}
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-black uppercase tracking-widest mb-2">
              <IconCash size={18} className="text-emerald-500" /> Total Collected
            </div>
            <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
              <span className="text-lg font-normal text-gray-400 mr-1">{currencySymbol}</span>
              {metrics.totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] text-gray-400 font-medium mt-2">Payments received from customers</p>
          </div>

          {/* Card 4: Customers with Dues */}
          <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-black uppercase tracking-widest mb-2">
              <IconUsers size={18} className="text-amber-500" /> Debtors / Open Accounts
            </div>
            <div className="text-3xl font-black text-amber-600 dark:text-amber-400 tracking-tight">
              {metrics.customersWithDue} <span className="text-base font-normal text-gray-400">clients</span>
            </div>
            <p className="text-[11px] text-gray-400 font-medium mt-2">Customers with pending credit balance</p>
          </div>
        </div>
      )}

      {/* ──────────────── Search & Date Filter Bar ──────────────── */}
      {activeTab !== "statement" && (
        <div className="flex flex-col md:flex-row flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center bg-white dark:bg-restro-gray px-4 py-2.5 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex-1 min-w-[280px] max-w-md">
            <IconSearch size={20} className="text-gray-400 mr-3" />
            <input
              type="text"
              placeholder="Search by customer name, phone, or invoice #..."
              className="bg-transparent border-none focus:ring-0 text-sm w-full outline-none"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCustomerPage(1);
                setInvoicesPage(1);
              }}
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setCustomerPage(1);
                  setInvoicesPage(1);
                }}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <IconX size={16} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={selectedCustomerForStatement?.customer_id || ""}
              onChange={(e) => {
                const c = customerList.find(cust => String(cust.phone || cust.id) === String(e.target.value));
                if (c) {
                  btnOpenStatement({
                    customer_id: c.phone || c.id,
                    name: c.name,
                    phone: c.phone,
                    email: c.email,
                    address: c.address
                  });
                }
              }}
              className="select select-sm select-bordered rounded-2xl bg-white dark:bg-restro-gray text-xs font-semibold h-11"
            >
              <option value="">Quick Ledger: Select any customer...</option>
              {customerList.map((c) => (
                <option key={c.phone || c.id} value={c.phone || c.id}>{c.name} {c.phone ? `(${c.phone})` : ""}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center bg-white dark:bg-restro-gray px-3 py-2 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm gap-2 text-xs">
            <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px] pl-2">Date:</span>
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => {
                setDateFilter({ ...dateFilter, from: e.target.value });
                setCustomerPage(1);
                setInvoicesPage(1);
              }}
              className="bg-transparent border-none focus:ring-0 text-xs py-1.5 px-2 outline-none font-bold"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateFilter.to}
              onChange={(e) => {
                setDateFilter({ ...dateFilter, to: e.target.value });
                setCustomerPage(1);
                setInvoicesPage(1);
              }}
              className="bg-transparent border-none focus:ring-0 text-xs py-1.5 px-2 outline-none font-bold"
            />
            {(dateFilter.from || dateFilter.to) && (
              <button
                onClick={() => {
                  setDateFilter({ from: "", to: "" });
                  setCustomerPage(1);
                  setInvoicesPage(1);
                }}
                className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-bold flex items-center gap-1 transition text-xs"
              >
                <IconX size={12} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* ──────────────── VIEW TAB 1: BY CUSTOMER (DEFAULT) ──────────────── */}
      {activeTab === "customers" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {isLoadingCustomerDues && (
              <div className="col-span-full py-24 text-center">
                <span className="loading loading-spinner loading-lg text-blue-600"></span>
                <p className="text-gray-400 text-xs mt-3 font-bold uppercase tracking-widest">Loading Customer Receivables...</p>
              </div>
            )}

            {!isLoadingCustomerDues && (!customerDues || customerDues.length === 0) && (
              <div className="col-span-full py-28 text-center bg-white dark:bg-restro-gray border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-[2.5rem]">
                <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-950/40 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-500">
                  <IconCheck size={36} stroke={2.5} />
                </div>
                <h4 className="text-xl font-black text-gray-800 dark:text-gray-200">All Customer Accounts Cleared!</h4>
                <p className="text-gray-400 text-sm mt-1 max-w-sm mx-auto">
                  No outstanding credit dues or unpaid customer balances found.
                </p>
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-2">Want to inspect a past ledger?</p>
                  <select
                    onChange={(e) => {
                      const c = customerList.find(cust => String(cust.phone || cust.id) === String(e.target.value));
                      if (c) {
                        btnOpenStatement({
                          customer_id: c.phone || c.id,
                          name: c.name,
                          phone: c.phone,
                          email: c.email,
                          address: c.address
                        });
                      }
                    }}
                    className="select select-sm select-bordered rounded-xl"
                  >
                    <option value="">Select Customer to View Ledger...</option>
                    {customerList.map(c => <option key={c.phone || c.id} value={c.phone || c.id}>{c.name} {c.phone ? `(${c.phone})` : ""}</option>)}
                  </select>
                </div>
              </div>
            )}

            {paginatedCustomerDues?.map((cust) => {
              const dueAmt = Number(String(cust.total_due).replace(/,/g, "")) || 0;
              const invoicedAmt = Number(String(cust.total_invoiced).replace(/,/g, "")) || 0;
              const paidAmt = Number(String(cust.total_paid).replace(/,/g, "")) || 0;

              return (
                <div
                  key={cust.customer_id}
                  className="relative overflow-hidden bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 rounded-3xl shadow-sm flex flex-col justify-between"
                >
                  <div className="h-1.5 bg-blue-600"></div>

                  <div className="p-6">
                    {/* Top line with Avatar and Tag */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-black text-lg shadow-sm border border-blue-100 dark:border-blue-900/30">
                          {cust.customer_name?.[0]?.toUpperCase() || "C"}
                        </div>
                        <div>
                          <h4 className="text-lg font-black text-gray-900 dark:text-white leading-tight truncate max-w-[170px]" title={cust.customer_name}>
                            {cust.customer_name}
                          </h4>
                          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                            <IconPhone size={12} /> {cust.customer_id || "No phone"}
                          </p>
                        </div>
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border border-blue-100 dark:border-blue-900/30">
                        {cust.invoice_count || 0} Invoices Due
                      </span>
                    </div>

                    {/* Customer details pill */}
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-5 flex flex-wrap gap-x-3 gap-y-1">
                      {cust.customer_pan && (
                        <span className="flex items-center gap-1 font-semibold">
                          <IconId size={13} className="text-gray-400" /> PAN: {cust.customer_pan}
                        </span>
                      )}
                      {cust.customer_address && (
                        <span className="truncate max-w-[200px]" title={cust.customer_address}>
                          {cust.customer_address}
                        </span>
                      )}
                    </div>

                    {/* Financial Breakdown card */}
                    <div className="bg-gray-50 dark:bg-gray-900/40 p-4 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-2 mb-4">
                      {invoicedAmt > 0 && (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Total Billed</span>
                          <span className="font-bold text-gray-800 dark:text-gray-200">
                            {currencySymbol} {invoicedAmt.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {paidAmt > 0 && (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Total Paid</span>
                          <span className="font-bold text-emerald-600">
                            {currencySymbol} {paidAmt.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-800 flex justify-between items-center">
                        <span className="text-[11px] font-black uppercase tracking-wider text-blue-600">
                          Outstanding Receivable
                        </span>
                        <span className="text-xl font-black text-blue-600">
                          {currencySymbol} {dueAmt.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions bottom bar */}
                  <div className="p-6 pt-0 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => btnOpenStatement(cust)}
                      className="py-3 px-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <IconReceipt2 size={16} /> Statement
                    </button>
                    <button
                      onClick={() => btnShowBulkCollectModal(cust)}
                      className="py-3 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                    >
                      <IconCash size={16} /> Settle (FIFO)
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Customer Tab Pagination controls */}
          {customerDues?.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 border border-gray-100 dark:border-gray-800 rounded-2xl bg-white dark:bg-restro-gray">
              <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                <span>
                  Showing {((safeCustomerPage - 1) * customerPageSize) + 1} to {Math.min(safeCustomerPage * customerPageSize, customerDues.length)} of {customerDues.length} clients
                </span>
                <select
                  value={customerPageSize}
                  onChange={(e) => {
                    setCustomerPageSize(Number(e.target.value));
                    setCustomerPage(1);
                  }}
                  className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
                >
                  <option value={6}>6 / page</option>
                  <option value={12}>12 / page</option>
                  <option value={24}>24 / page</option>
                  <option value={60}>60 / page</option>
                </select>
              </div>
              <div className="join">
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCustomerPage === 1}
                  onClick={() => setCustomerPage(1)}
                  title="First Page"
                >
                  <IconChevronsLeft size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCustomerPage === 1}
                  onClick={() => setCustomerPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <IconChevronLeft size={14} />
                </button>
                <button className="join-item btn btn-xs no-animation font-semibold">
                  Page {safeCustomerPage} of {totalCustomerPages}
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCustomerPage >= totalCustomerPages}
                  onClick={() => setCustomerPage((p) => Math.min(totalCustomerPages, p + 1))}
                  title="Next Page"
                >
                  <IconChevronRight size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCustomerPage >= totalCustomerPages}
                  onClick={() => setCustomerPage(totalCustomerPages)}
                  title="Last Page"
                >
                  <IconChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──────────────── VIEW TAB 2: ALL INDIVIDUAL DUE INVOICES ──────────────── */}
      {activeTab === "invoices" && (
        <div className="bg-white dark:bg-restro-gray rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-800 text-[11px] font-black uppercase text-gray-400 tracking-wider">
                  <th className="py-4 px-6">Invoice #</th>
                  <th>Customer Name</th>
                  <th>Customer ID / Phone</th>
                  <th>Date</th>
                  <th>Total Bill</th>
                  <th>Paid Amount</th>
                  <th>Due Amount</th>
                  <th>Status</th>
                  <th className="text-right px-6">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                {(!paginatedDueInvoices || paginatedDueInvoices.length === 0) && (
                  <tr>
                    <td colSpan={9} className="py-20 text-center text-gray-400">
                      No outstanding sales invoices found matching your filter.
                    </td>
                  </tr>
                )}
                {paginatedDueInvoices.map((inv) => {
                  const total = Number(String(inv.total).replace(/,/g, "")) || 0;
                  const paid = Number(String(inv.paid_amount).replace(/,/g, "")) || 0;
                  const due = Number(String(inv.due_amount).replace(/,/g, "")) || 0;

                  return (
                    <tr key={inv.invoice_id} className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors">
                      <td className="py-4 px-6 font-mono font-bold text-xs">
                        <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                          INV-#{inv.invoice_id}
                        </span>
                      </td>
                      <td className="font-bold text-gray-900 dark:text-white">
                        {inv.name || "Walk-in Customer"}
                      </td>
                      <td className="text-xs text-gray-500 font-mono">
                        {inv.customer_id || "-"}
                      </td>
                      <td className="text-xs text-gray-500">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "-"}
                      </td>
                      <td className="font-semibold text-gray-700 dark:text-gray-300">
                        {currencySymbol} {total.toFixed(2)}
                      </td>
                      <td className="text-emerald-600 font-bold">
                        {currencySymbol} {paid.toFixed(2)}
                      </td>
                      <td className="text-blue-600 font-black text-base">
                        {currencySymbol} {due.toFixed(2)}
                      </td>
                      <td>
                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          paid > 0
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'
                        }`}>
                          {paid > 0 ? 'Partial' : 'Unpaid'}
                        </span>
                      </td>
                      <td className="text-right px-6">
                        <button
                          onClick={() => btnShowSingleCollectModal(inv)}
                          className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
                        >
                          Collect Due
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Invoices Tab Pagination controls */}
          {dueInvoices?.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-restro-gray">
              <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                <span>
                  Showing {((safeInvoicesPage - 1) * invoicesPageSize) + 1} to {Math.min(safeInvoicesPage * invoicesPageSize, dueInvoices.length)} of {dueInvoices.length} invoices
                </span>
                <select
                  value={invoicesPageSize}
                  onChange={(e) => {
                    setInvoicesPageSize(Number(e.target.value));
                    setInvoicesPage(1);
                  }}
                  className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
              <div className="join">
                <button
                  className="join-item btn btn-xs"
                  disabled={safeInvoicesPage === 1}
                  onClick={() => setInvoicesPage(1)}
                  title="First Page"
                >
                  <IconChevronsLeft size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeInvoicesPage === 1}
                  onClick={() => setInvoicesPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <IconChevronLeft size={14} />
                </button>
                <button className="join-item btn btn-xs no-animation font-semibold">
                  Page {safeInvoicesPage} of {totalInvoicesPages}
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeInvoicesPage >= totalInvoicesPages}
                  onClick={() => setInvoicesPage((p) => Math.min(totalInvoicesPages, p + 1))}
                  title="Next Page"
                >
                  <IconChevronRight size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeInvoicesPage >= totalInvoicesPages}
                  onClick={() => setInvoicesPage(totalInvoicesPages)}
                  title="Last Page"
                >
                  <IconChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──────────────── VIEW TAB 3: CUSTOMER STATEMENT & LEDGER ──────────────── */}
      {activeTab === "statement" && selectedCustomerForStatement && (
        <div className="space-y-6">
          {/* Customer Header Profile Card */}
          <div className="bg-white dark:bg-restro-gray rounded-[2rem] border border-gray-100 dark:border-gray-800 p-6 md:p-8 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-black text-2xl shadow-md">
                {(selectedCustomerForStatement.customer_name || selectedCustomerForStatement.name)?.[0]?.toUpperCase() || "C"}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white">
                    {selectedCustomerForStatement.customer_name || selectedCustomerForStatement.name}
                  </h3>
                  <span className="text-xs px-2.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 font-bold rounded-full">
                    Debtor Ledger
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {(selectedCustomerForStatement.customer_id || selectedCustomerForStatement.phone) && (
                    <span className="flex items-center gap-1 font-semibold">
                      <IconPhone size={13} /> {selectedCustomerForStatement.customer_id || selectedCustomerForStatement.phone}
                    </span>
                  )}
                  {selectedCustomerForStatement.customer_pan && (
                    <span className="flex items-center gap-1 font-semibold">
                      <IconId size={13} /> PAN: {selectedCustomerForStatement.customer_pan}
                    </span>
                  )}
                  {selectedCustomerForStatement.customer_email && (
                    <span className="flex items-center gap-1">
                      <IconMail size={13} /> {selectedCustomerForStatement.customer_email}
                    </span>
                  )}
                  {selectedCustomerForStatement.customer_address && (
                    <span>{selectedCustomerForStatement.customer_address}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Live Balance & Quick Action */}
            <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 pt-4 md:pt-0 border-gray-100 dark:border-gray-800">
              <div className="text-right">
                <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Total Receivable Due</span>
                <div className="text-2xl font-black text-blue-600">
                  {currencySymbol} {(Number(statementData?.summary?.net_due) || 0).toFixed(2)}
                </div>
              </div>
              <button
                onClick={() => btnShowBulkCollectModal(selectedCustomerForStatement)}
                className="py-3 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-sm flex items-center gap-2 transition shadow-lg shadow-blue-200 dark:shadow-none"
              >
                <IconCash size={18} /> Settle (FIFO)
              </button>
            </div>
          </div>

          {/* Statement Transaction Ledger Table */}
          <div className="bg-white dark:bg-restro-gray rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <div>
                <h4 className="font-black text-lg text-gray-900 dark:text-white">Transaction Statement</h4>
                <p className="text-gray-400 text-xs">Chronological record of invoices, receipts, and running balance</p>
              </div>
              <div className="text-xs text-gray-500 font-bold">
                Total Transactions: {statementData?.transactions?.length || 0}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="table w-full text-left text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-800 text-[11px] font-black uppercase text-gray-400 tracking-wider">
                    <th className="py-4 px-6">Date</th>
                    <th>Voucher / Type</th>
                    <th>Reference #</th>
                    <th>Description</th>
                    <th className="text-right">Debit (Invoice)</th>
                    <th className="text-right">Credit (Receipt)</th>
                    <th className="text-right">Running Balance</th>
                    <th className="text-right px-6">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {isLoadingStatement && (
                    <tr>
                      <td colSpan={8} className="py-16 text-center text-gray-400 font-bold">
                        <span className="loading loading-spinner text-blue-600"></span>
                        <p className="mt-2 text-xs">Loading Statement...</p>
                      </td>
                    </tr>
                  )}

                  {!isLoadingStatement && (!statementTransactions || statementTransactions.length === 0) && (
                    <tr>
                      <td colSpan={8} className="py-16 text-center text-gray-400">
                        No transactions recorded for this customer.
                      </td>
                    </tr>
                  )}

                  {paginatedStatementTransactions?.map((t, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors">
                      <td className="py-3.5 px-6 font-medium text-xs text-gray-600 dark:text-gray-400">
                        {t.date ? new Date(t.date).toLocaleDateString() : "-"}
                      </td>
                      <td>
                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                          t.credit > 0
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                        }`}>
                          {t.type}
                        </span>
                      </td>
                      <td className="font-mono text-xs font-bold text-gray-800 dark:text-gray-200">
                        {t.ref_no || "-"}
                      </td>
                      <td className="text-xs text-gray-600 dark:text-gray-400 max-w-xs truncate" title={t.description}>
                        {t.description || "-"}
                      </td>
                      <td className="text-right font-semibold text-gray-800 dark:text-gray-200">
                        {t.debit > 0 ? `${currencySymbol} ${Number(t.debit).toFixed(2)}` : "-"}
                      </td>
                      <td className="text-right font-bold text-emerald-600">
                        {t.credit > 0 ? `${currencySymbol} ${Number(t.credit).toFixed(2)}` : "-"}
                      </td>
                      <td className="text-right font-black text-gray-900 dark:text-white">
                        {currencySymbol} {Number(t.balance || 0).toFixed(2)}
                      </td>
                      <td className="text-right px-6">
                        <div className="flex items-center justify-end gap-1.5">
                          {t.debit > 0 ? (
                            <>
                              <button
                                onClick={() => btnViewInvoiceDetails(t.raw_id || t.invoice_id)}
                                className="p-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/50 text-blue-600 transition"
                                title="View Invoice Details"
                              >
                                <IconEye size={15} />
                              </button>
                              <button
                                onClick={() => btnPrintInvoiceThermal(t.raw_id || t.invoice_id, t)}
                                className="p-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 text-indigo-600 transition"
                                title="Print Invoice (Thermal Printer)"
                              >
                                <IconPrinter size={15} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setClearedPaymentReceipt({
                                    voucherNo: t.id || `PAY-#${t.raw_id}`,
                                    title: 'PAYMENT VOUCHER',
                                    date: t.date,
                                    partyName: selectedCustomerForStatement?.customer_name || selectedCustomerForStatement?.name,
                                    partyPhone: selectedCustomerForStatement?.customer_id || selectedCustomerForStatement?.phone,
                                    partyType: 'Customer',
                                    invoicesSettled: t.invoice_id ? [{ invoice_id: t.invoice_id, allocated: t.credit }] : [],
                                    paymentMethods: [{ method: t.payment_method || 'Cash', amount: t.credit }],
                                    totalAmount: t.credit,
                                    remainingDue: t.balance,
                                    notes: t.notes || t.description
                                  });
                                  document.getElementById("modal-payment-clearance-voucher").showModal();
                                }}
                                className="p-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 transition"
                                title="View Payment Voucher"
                              >
                                <IconEye size={15} />
                              </button>
                              <button
                                onClick={() => {
                                  btnPrintPaymentVoucherReceipt({
                                    voucherNo: t.id || `PAY-#${t.raw_id}`,
                                    title: 'PAYMENT VOUCHER',
                                    date: t.date,
                                    partyName: selectedCustomerForStatement?.customer_name || selectedCustomerForStatement?.name,
                                    partyPhone: selectedCustomerForStatement?.customer_id || selectedCustomerForStatement?.phone,
                                    partyType: 'Customer',
                                    invoicesSettled: t.invoice_id ? [{ invoice_id: t.invoice_id, allocated: t.credit }] : [],
                                    paymentMethods: [{ method: t.payment_method || 'Cash', amount: t.credit }],
                                    totalAmount: t.credit,
                                    remainingDue: t.balance,
                                    notes: t.notes || t.description
                                  });
                                }}
                                className="p-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 transition"
                                title="Print Payment Voucher (Thermal Printer)"
                              >
                                <IconPrinter size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Statement Pagination controls */}
            {statementTransactions?.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-restro-gray">
                <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                  <span>
                    Showing {((safeStatementPage - 1) * statementPageSize) + 1} to {Math.min(safeStatementPage * statementPageSize, statementTransactions.length)} of {statementTransactions.length} entries
                  </span>
                  <select
                    value={statementPageSize}
                    onChange={(e) => {
                      setStatementPageSize(Number(e.target.value));
                      setStatementPage(1);
                    }}
                    className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
                  >
                    <option value={15}>15 / page</option>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
                <div className="join">
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage === 1}
                    onClick={() => setStatementPage(1)}
                    title="First Page"
                  >
                    <IconChevronsLeft size={14} />
                  </button>
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage === 1}
                    onClick={() => setStatementPage((p) => Math.max(1, p - 1))}
                    title="Previous Page"
                  >
                    <IconChevronLeft size={14} />
                  </button>
                  <button className="join-item btn btn-xs no-animation font-semibold">
                    Page {safeStatementPage} of {totalStatementPages}
                  </button>
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage >= totalStatementPages}
                    onClick={() => setStatementPage((p) => Math.min(totalStatementPages, p + 1))}
                    title="Next Page"
                  >
                    <IconChevronRight size={14} />
                  </button>
                  <button
                    className="join-item btn btn-xs"
                    disabled={safeStatementPage >= totalStatementPages}
                    onClick={() => setStatementPage(totalStatementPages)}
                    title="Last Page"
                  >
                    <IconChevronsRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Statement Summary Footer Box */}
            {statementData?.summary && (
              <div className="p-6 bg-gray-50 dark:bg-gray-900/60 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-xs text-gray-400">
                  Showing complete customer ledger up to current date.
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-gray-400 text-xs mr-2">Total Invoiced:</span>
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                      {currencySymbol} {Number(statementData.summary.total_invoiced).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs mr-2">Total Collected:</span>
                    <span className="font-bold text-emerald-600">
                      {currencySymbol} {Number(statementData.summary.total_received).toFixed(2)}
                    </span>
                  </div>
                  <div className="pl-4 border-l border-gray-200 dark:border-gray-700">
                    <span className="text-gray-400 text-xs mr-2 font-bold uppercase">Net Receivable:</span>
                    <span className="font-black text-blue-600 text-base">
                      {currencySymbol} {Number(statementData.summary.net_due).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ──────────────── MODAL 1: BULK FIFO SETTLEMENT MODAL ──────────────── */}
      <dialog id="modal-bulk-collect-fifo" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white relative shrink-0">
            <h3 className="text-2xl font-black mb-0.5">Collect Customer Dues (FIFO)</h3>
            <p className="text-blue-100 text-xs">
              Payments automatically clear unpaid invoices starting from the oldest invoice date
            </p>
            <button
              onClick={() => document.getElementById("modal-bulk-collect-fifo").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto flex-1 overscroll-contain">
            {/* Selected Customer Banner */}
            {selectedCustomerForBulkPay && (
              <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/40 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Customer</p>
                  <p className="font-black text-base text-gray-900 dark:text-white">
                    {selectedCustomerForBulkPay.customer_name || selectedCustomerForBulkPay.name}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">
                    {selectedCustomerForBulkPay.customer_id || selectedCustomerForBulkPay.phone || "No phone"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Total Due</p>
                  <p className="text-blue-600 font-black text-xl">
                    {currencySymbol} {(Number(selectedCustomerForBulkPay.total_due) || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {/* Amount input & Quick helpers */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-gray-500">
                  Collection Amount
                </label>
                {selectedCustomerForBulkPay && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPayAmount(Number(selectedCustomerForBulkPay.total_due || 0).toFixed(2))}
                      className="text-[11px] font-bold text-blue-600 hover:underline"
                    >
                      Collect Full ({currencySymbol}{Number(selectedCustomerForBulkPay.total_due || 0).toFixed(2)})
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition">
                <span className="px-4 py-3.5 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-black text-lg select-none flex items-center justify-center shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full bg-transparent py-3.5 px-4 font-black text-2xl text-gray-900 dark:text-white outline-none"
                />
              </div>
            </div>

            {/* ─── LIVE FIFO ALLOCATION PREVIEW BOX ─── */}
            {fifoAllocations.length > 0 && (
              <div className="bg-slate-50 dark:bg-gray-900/80 p-4 rounded-2xl border border-slate-200 dark:border-gray-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                    <IconLayersSubtract size={15} className="text-blue-500" /> FIFO Invoice Allocation Preview
                  </span>
                  <span className="text-[10px] text-gray-400">Oldest to newest</span>
                </div>

                <div className="max-h-44 overflow-y-auto space-y-1.5 text-xs pr-1">
                  {fifoAllocations.map((alloc, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
                    >
                      <div>
                        <div className="flex items-center gap-1.5 font-bold">
                          <span className="font-mono text-gray-800 dark:text-gray-200">INV-#{alloc.invoice_id}</span>
                          <span className="text-[10px] text-gray-400">({new Date(alloc.date).toLocaleDateString()})</span>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          Due: {currencySymbol}{alloc.original_due.toFixed(2)} → Settling: <span className="font-bold text-emerald-600">{currencySymbol}{alloc.allocated.toFixed(2)}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        {alloc.is_cleared ? (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 rounded-md">
                            Cleared ✓
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-amber-600">
                            Rem: {currencySymbol}{alloc.remaining_due.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Remaining Balance Summary */}
                <div className="pt-2 border-t border-slate-200 dark:border-gray-700 flex justify-between text-xs font-bold text-gray-600 dark:text-gray-300">
                  <span>Remaining Customer Debt:</span>
                  <span className="text-blue-600 font-black">
                    {currencySymbol} {Math.max(0, (Number(selectedCustomerForBulkPay?.total_due || 0) - (parseFloat(payAmount) || 0))).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Date & Payment Method */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Collection Date
                </label>
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold focus:ring-2 ring-blue-500 outline-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-black uppercase tracking-wider text-gray-500 block">
                    Payment Method
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !isSplitPayment;
                      setIsSplitPayment(next);
                      if (next && (!splitPayments || splitPayments.length <= 1)) {
                        setSplitPayments([
                          { method: payMethod || "Cash", amount: parseFloat(payAmount) || 0 }
                        ]);
                      }
                    }}
                    className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold transition flex items-center gap-1 ${
                      isSplitPayment
                        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    {isSplitPayment ? "✓ Split Mode" : "+ Split Pay"}
                  </button>
                </div>
                {!isSplitPayment ? (
                  <select
                    value={payMethod}
                    onChange={(e) => setPayMethod(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold focus:ring-2 ring-blue-500 outline-none"
                  >
                    {paymentTypes?.filter((pt) => pt.is_active).map((pt) => (
                      <option key={pt.id} value={pt.title}>{pt.title}</option>
                    ))}
                    {(!paymentTypes || paymentTypes.filter((pt) => pt.is_active).length === 0) && (
                      <>
                        <option value="Cash">Cash</option>
                        <option value="QR">QR Payment</option>
                        <option value="Card">Card</option>
                        <option value="Bank">Bank Transfer</option>
                      </>
                    )}
                  </select>
                ) : (
                  <div className="text-xs text-amber-600 dark:text-amber-400 font-bold py-2">
                    Multi-payment configured below
                  </div>
                )}
              </div>
            </div>

            {/* Split Payment Detailed Section if enabled */}
            {isSplitPayment && (
              <div className="bg-amber-50/70 dark:bg-amber-950/20 p-4 rounded-2xl border border-amber-200/80 dark:border-amber-900/40 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase text-amber-800 dark:text-amber-400">Split Breakdown</span>
                  <button
                    type="button"
                    onClick={handleAddSplitRow}
                    className="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1 hover:underline"
                  >
                    <IconPlus size={13} /> Add Method
                  </button>
                </div>
                <div className="space-y-2">
                  {splitPayments.map((row, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <select
                        value={row.method}
                        onChange={(e) => handleUpdateSplitRow(idx, "method", e.target.value)}
                        className="flex-1 bg-white dark:bg-gray-900 border border-amber-200 dark:border-gray-800 rounded-xl py-2 px-2.5 text-xs font-bold outline-none"
                      >
                        {paymentTypes?.filter((pt) => pt.is_active).map((pt) => (
                          <option key={pt.id} value={pt.title}>{pt.title}</option>
                        ))}
                        {(!paymentTypes || paymentTypes.filter((pt) => pt.is_active).length === 0) && (
                          <>
                            <option value="Cash">Cash</option>
                            <option value="QR">QR Payment</option>
                            <option value="Card">Card</option>
                            <option value="Bank">Bank Transfer</option>
                          </>
                        )}
                      </select>
                      <div className="flex items-center w-36 bg-white dark:bg-gray-900 border border-amber-200 dark:border-gray-800 rounded-xl overflow-hidden px-2">
                        <span className="text-[10px] text-gray-400 font-bold mr-1">{currencySymbol}</span>
                        <input
                          type="number"
                          step="any"
                          value={row.amount}
                          onChange={(e) => handleUpdateSplitRow(idx, "amount", e.target.value)}
                          className="w-full bg-transparent py-2 text-xs font-black outline-none"
                          placeholder="0.00"
                        />
                      </div>
                      {splitPayments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSplitRow(idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition"
                        >
                          <IconTrash size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center text-xs font-bold text-amber-800 dark:text-amber-300 pt-1 border-t border-amber-200/60 dark:border-amber-900/40">
                  <span>Split Total:</span>
                  <span className="font-mono font-black">{currencySymbol} {splitPayments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0).toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Notes / Reference */}
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Reference / Notes
              </label>
              <textarea
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                placeholder="Transaction Ref, Check #, Note..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs focus:ring-2 ring-blue-500 outline-none h-16 resize-none"
              ></textarea>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-bulk-collect-fifo").close()}
                className="flex-1 py-3.5 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200 dark:hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingPay}
                onClick={btnProcessBulkFIFOSettlement}
                className="flex-[1.5] py-3.5 font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-2xl transition shadow-lg shadow-blue-200 dark:shadow-none flex items-center justify-center gap-2"
              >
                {isSubmittingPay ? <span className="loading loading-spinner loading-xs"></span> : <IconCheck size={16} />}
                Confirm Collection
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ──────────────── MODAL 2: SINGLE INVOICE COLLECT MODAL ──────────────── */}
      <dialog id="modal-collect-due" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md max-h-[90vh] flex flex-col">
          <div className="bg-gray-900 p-6 text-white relative shrink-0">
            <h3 className="text-xl font-black mb-0.5">Collect Invoice Due</h3>
            <p className="text-gray-400 text-xs">Record payment for individual sale invoice</p>
            <button
              onClick={() => document.getElementById("modal-collect-due").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 overscroll-contain">
            {selectedInvoice && (
              <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/30 flex justify-between items-center">
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Customer</p>
                  <p className="font-bold text-sm text-gray-900 dark:text-white">{selectedInvoice.name || "Walk-in"}</p>
                  <p className="text-xs text-gray-500 font-mono">INV-#{selectedInvoice.invoice_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Due Amount</p>
                  <p className="text-blue-600 font-black text-lg">
                    {currencySymbol} {(Number(selectedInvoice.due_amount) || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Amount to Collect
              </label>
              <div className="flex items-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition">
                <span className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-black text-base select-none flex items-center justify-center shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  step="any"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full bg-transparent py-3 px-4 font-black text-xl text-gray-900 dark:text-white outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Date</label>
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-3 text-xs font-bold outline-none"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-black uppercase tracking-wider text-gray-500 block">Method</label>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !isSplitPayment;
                      setIsSplitPayment(next);
                      if (next && (!splitPayments || splitPayments.length <= 1)) {
                        setSplitPayments([
                          { method: payMethod || "Cash", amount: parseFloat(payAmount) || 0 }
                        ]);
                      }
                    }}
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold transition flex items-center gap-1 ${
                      isSplitPayment
                        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    {isSplitPayment ? "✓ Split Mode" : "+ Split Pay"}
                  </button>
                </div>
                {!isSplitPayment ? (
                  <select
                    value={payMethod}
                    onChange={(e) => setPayMethod(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-3 text-xs font-bold outline-none"
                  >
                    {paymentTypes?.filter((pt) => pt.is_active).map((pt) => (
                      <option key={pt.id} value={pt.title}>{pt.title}</option>
                    ))}
                    {(!paymentTypes || paymentTypes.filter((pt) => pt.is_active).length === 0) && (
                      <>
                        <option value="Cash">Cash</option>
                        <option value="QR">QR Payment</option>
                        <option value="Card">Card</option>
                        <option value="Bank">Bank Transfer</option>
                      </>
                    )}
                  </select>
                ) : (
                  <div className="text-xs text-amber-600 dark:text-amber-400 font-bold py-2">
                    Multi-payment configured below
                  </div>
                )}
              </div>
            </div>

            {/* Split Payment Detailed Section if enabled */}
            {isSplitPayment && (
              <div className="bg-amber-50/70 dark:bg-amber-950/20 p-4 rounded-2xl border border-amber-200/80 dark:border-amber-900/40 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase text-amber-800 dark:text-amber-400">Split Breakdown</span>
                  <button
                    type="button"
                    onClick={handleAddSplitRow}
                    className="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1 hover:underline"
                  >
                    <IconPlus size={13} /> Add Method
                  </button>
                </div>
                <div className="space-y-2">
                  {splitPayments.map((row, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <select
                        value={row.method}
                        onChange={(e) => handleUpdateSplitRow(idx, "method", e.target.value)}
                        className="flex-1 bg-white dark:bg-gray-900 border border-amber-200 dark:border-gray-800 rounded-xl py-2 px-2.5 text-xs font-bold outline-none"
                      >
                        {paymentTypes?.filter((pt) => pt.is_active).map((pt) => (
                          <option key={pt.id} value={pt.title}>{pt.title}</option>
                        ))}
                        {(!paymentTypes || paymentTypes.filter((pt) => pt.is_active).length === 0) && (
                          <>
                            <option value="Cash">Cash</option>
                            <option value="QR">QR Payment</option>
                            <option value="Card">Card</option>
                            <option value="Bank">Bank Transfer</option>
                          </>
                        )}
                      </select>
                      <div className="flex items-center w-36 bg-white dark:bg-gray-900 border border-amber-200 dark:border-gray-800 rounded-xl overflow-hidden px-2">
                        <span className="text-[10px] text-gray-400 font-bold mr-1">{currencySymbol}</span>
                        <input
                          type="number"
                          step="any"
                          value={row.amount}
                          onChange={(e) => handleUpdateSplitRow(idx, "amount", e.target.value)}
                          className="w-full bg-transparent py-2 text-xs font-black outline-none"
                          placeholder="0.00"
                        />
                      </div>
                      {splitPayments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSplitRow(idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition"
                        >
                          <IconTrash size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center text-xs font-bold text-amber-800 dark:text-amber-300 pt-1 border-t border-amber-200/60 dark:border-amber-900/40">
                  <span>Split Total:</span>
                  <span className="font-mono font-black">{currencySymbol} {splitPayments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0).toFixed(2)}</span>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Notes</label>
              <textarea
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                placeholder="Transaction details..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2 px-3 text-xs outline-none h-16 resize-none"
              ></textarea>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-collect-due").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingPay}
                onClick={btnProcessSingleCollection}
                className="flex-[1.5] py-3 font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-2xl transition shadow-md"
              >
                {isSubmittingPay ? <span className="loading loading-spinner loading-xs"></span> : "Confirm Collection"}
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ──────────────── MODAL 3: MANUAL DUE ENTRY MODAL ──────────────── */}
      <dialog id="modal-manual-due" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md max-h-[90vh] flex flex-col">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white relative shrink-0">
            <h3 className="text-xl font-black mb-0.5">Add Manual Customer Due</h3>
            <p className="text-blue-100 text-xs">Record an adjustment or opening credit balance</p>
            <button
              onClick={() => document.getElementById("modal-manual-due").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 overscroll-contain">
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Customer</label>
              <select
                value={manualDueForm.customer_id}
                onChange={(e) => setManualDueForm({ ...manualDueForm, customer_id: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold outline-none"
              >
                <option value="">Select Customer</option>
                {customerList.map((c) => (
                  <option key={c.phone || c.id} value={c.phone}>{c.name} ({c.phone || "No phone"})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Due Amount</label>
              <div className="flex items-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition">
                <span className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-black text-sm select-none flex items-center justify-center shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  step="any"
                  value={manualDueForm.amount}
                  onChange={(e) => setManualDueForm({ ...manualDueForm, amount: e.target.value })}
                  className="w-full bg-transparent py-3 px-4 font-black text-lg text-gray-900 dark:text-white outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Date</label>
              <input
                type="date"
                value={manualDueForm.date}
                onChange={(e) => setManualDueForm({ ...manualDueForm, date: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs font-bold outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">Reference / Description</label>
              <input
                type="text"
                value={manualDueForm.notes}
                onChange={(e) => setManualDueForm({ ...manualDueForm, notes: e.target.value })}
                placeholder="Reason or invoice reference..."
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-3 px-4 text-xs outline-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-manual-due").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={btnProcessManualDue}
                className="flex-[1.5] py-3 font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-2xl transition shadow-md"
              >
                Save Due
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ──────────────── MODAL 4: PAYMENT CLEARANCE VOUCHER / RECEIPT ──────────────── */}
      <dialog id="modal-payment-clearance-voucher" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md max-h-[90vh] flex flex-col bg-white dark:bg-restro-gray">
          <div className="bg-emerald-600 p-6 text-white relative shrink-0">
            <h3 className="text-xl font-black mb-0.5">{clearedPaymentReceipt?.title || 'Payment Receipt'}</h3>
            <p className="text-emerald-100 text-xs">Clearance voucher & remaining due details</p>
            <button
              onClick={() => document.getElementById("modal-payment-clearance-voucher").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 overscroll-contain">
            {clearedPaymentReceipt && (
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-gray-800">
                  <div>
                    <p className="text-[10px] text-gray-400 font-black uppercase">Voucher #</p>
                    <p className="font-mono font-bold text-sm text-gray-800 dark:text-gray-200">{clearedPaymentReceipt.voucherNo}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-gray-400 font-black uppercase">Date</p>
                    <p className="font-bold text-xs text-gray-700 dark:text-gray-300">
                      {moment(clearedPaymentReceipt.date).isValid() ? moment(clearedPaymentReceipt.date).format("MMM DD, YYYY") : clearedPaymentReceipt.date}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/60 p-3.5 rounded-2xl">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">{clearedPaymentReceipt.partyType || 'Party'}</p>
                  <p className="font-bold text-sm text-gray-900 dark:text-white">{clearedPaymentReceipt.partyName || 'Customer'}</p>
                  {clearedPaymentReceipt.partyPhone && (
                    <p className="text-xs text-gray-500 font-mono">{clearedPaymentReceipt.partyPhone}</p>
                  )}
                </div>

                {/* Cleared Invoices breakdown if any */}
                {clearedPaymentReceipt.invoicesSettled?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-gray-400 font-black uppercase mb-2">Settled Invoices</p>
                    <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 text-xs">
                      {clearedPaymentReceipt.invoicesSettled.map((inv, idx) => (
                        <div key={idx} className="p-2.5 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/30">
                          <div>
                            <span className="font-bold font-mono text-gray-800 dark:text-gray-200">INV-#{inv.invoice_id}</span>
                            {inv.remaining_due !== undefined && (
                              <span className="ml-2 text-[10px] text-gray-400">
                                (Remaining: {currencySymbol}{Number(inv.remaining_due).toFixed(2)})
                              </span>
                            )}
                          </div>
                          <span className="font-black text-emerald-600">
                            {currencySymbol} {Number(inv.allocated || inv.amount || 0).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Methods breakdown */}
                {clearedPaymentReceipt.paymentMethods?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-gray-400 font-black uppercase mb-2">Payment Method Breakdown</p>
                    <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl p-3 space-y-1.5 text-xs">
                      {clearedPaymentReceipt.paymentMethods.map((pm, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <span className="font-bold text-gray-700 dark:text-gray-300">{pm.method}</span>
                          <span className="font-black font-mono text-emerald-700 dark:text-emerald-400">
                            {currencySymbol} {Number(pm.amount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Total & Remaining Due cards */}
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="bg-emerald-50 dark:bg-emerald-950/30 p-3.5 rounded-2xl border border-emerald-100 dark:border-emerald-900/30">
                    <p className="text-[10px] text-gray-400 font-black uppercase">Cleared Amount</p>
                    <p className="font-black text-lg text-emerald-600">
                      {currencySymbol} {Number(clearedPaymentReceipt.totalAmount || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-3.5 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                    <p className="text-[10px] text-gray-400 font-black uppercase">Remaining Due</p>
                    <p className="font-black text-lg text-blue-600">
                      {currencySymbol} {Number(clearedPaymentReceipt.remainingDue || 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                {clearedPaymentReceipt.notes && (
                  <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-900 p-2.5 rounded-xl border border-gray-100 dark:border-gray-800">
                    <span className="font-bold">Note: </span>{clearedPaymentReceipt.notes}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => document.getElementById("modal-payment-clearance-voucher").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200 transition"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => btnPrintPaymentVoucherReceipt(clearedPaymentReceipt)}
                className="flex-[1.5] py-3 font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-700 rounded-2xl transition shadow-md flex items-center justify-center gap-2"
              >
                <IconPrinter size={16} />
                Print Thermal Voucher
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ──────────────── MODAL 5: INVOICE DETAIL VIEWER ──────────────── */}
      <dialog id="modal-invoice-detail-viewer" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-lg max-h-[90vh] flex flex-col bg-white dark:bg-restro-gray">
          <div className="bg-indigo-600 p-6 text-white relative shrink-0">
            <h3 className="text-xl font-black mb-0.5">
              Invoice #{viewingInvoiceDetails?.invoice_no || viewingInvoiceDetails?.invoiceId}
            </h3>
            <p className="text-indigo-100 text-xs">Itemized invoice breakdown</p>
            <button
              onClick={() => document.getElementById("modal-invoice-detail-viewer").close()}
              className="absolute right-5 top-5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <IconX size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 overscroll-contain">
            {viewingInvoiceDetails && (
              <div className="space-y-4 text-xs">
                {/* Orders / Items list */}
                {viewingInvoiceDetails.orders?.length > 0 ? (
                  <div className="border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden">
                    <table className="table table-xs w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th>Item</th>
                          <th className="text-center">Qty</th>
                          <th className="text-right">Price</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {viewingInvoiceDetails.orders.flatMap((o) => o.items || []).map((it, idx) => (
                          <tr key={idx}>
                            <td className="font-medium">
                              <div>{it.item_title}</div>
                              {it.variant_title && <div className="text-[10px] text-gray-400">Variant: {it.variant_title}</div>}
                            </td>
                            <td className="text-center font-mono font-bold">{it.quantity}</td>
                            <td className="text-right">{currencySymbol} {Number(it.price || it.variant_price || 0).toFixed(2)}</td>
                            <td className="text-right font-bold">{currencySymbol} {(Number(it.price || it.variant_price || 0) * Number(it.quantity || 1)).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-2xl text-center text-gray-400">
                    No individual order items recorded for this invoice.
                  </div>
                )}

                {/* Totals Summary */}
                <div className="bg-gray-50 dark:bg-gray-900/60 p-4 rounded-2xl space-y-2 border border-gray-100 dark:border-gray-800">
                  <div className="flex justify-between text-gray-500">
                    <span>Subtotal:</span>
                    <span>{currencySymbol} {Number(viewingInvoiceDetails.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {Number(viewingInvoiceDetails.taxTotal || 0) > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Tax / VAT:</span>
                      <span>{currencySymbol} {Number(viewingInvoiceDetails.taxTotal).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(viewingInvoiceDetails.discount || 0) > 0 && (
                    <div className="flex justify-between text-emerald-600 font-bold">
                      <span>Discount:</span>
                      <span>-{currencySymbol} {Number(viewingInvoiceDetails.discount).toFixed(2)}</span>
                    </div>
                  )}
                  {Number(viewingInvoiceDetails.roundOff || 0) !== 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Round Off:</span>
                      <span>{currencySymbol} {Number(viewingInvoiceDetails.roundOff).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-black text-sm text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span>Total Amount:</span>
                    <span>{currencySymbol} {Number(viewingInvoiceDetails.total || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600 font-bold">
                    <span>Paid Amount:</span>
                    <span>{currencySymbol} {Number(viewingInvoiceDetails.paidAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-blue-600 font-black">
                    <span>Due Balance:</span>
                    <span>{currencySymbol} {Number(viewingInvoiceDetails.dueAmount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-invoice-detail-viewer").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200 transition"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  if (viewingInvoiceDetails) {
                    btnPrintInvoiceThermal(viewingInvoiceDetails.invoiceId, null);
                  }
                }}
                className="flex-[1.5] py-3 font-bold text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded-2xl transition shadow-md flex items-center justify-center gap-2"
              >
                <IconPrinter size={16} />
                Print Thermal Invoice
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </Page>
  );
}
